import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Edit,
  Security,
  Person,
  Refresh,
  Shield,
  Email,
  History,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import EmailLogs from '../components/EmailLogs';
import AuditLogs from '../components/AuditLogs';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'hr' | 'manager' | 'viewer';
  is_active: boolean;
  last_login?: string;
  created_at: string;
}

interface Job {
  id: string;
  title: string;
  department: string;
  status: string;
}

interface JobPermission {
  id: string;
  user_id: string;
  job_id: string;
  permission_type: 'view' | 'edit' | 'manage';
  user_name?: string;
  job_title?: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [permissions, setPermissions] = useState<JobPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'viewer' as User['role'],
    password: '',
  });

  // Permission dialog state
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [permissionType, setPermissionType] = useState<'view' | 'edit' | 'manage'>('view');

  // Check if user is admin
  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch users
      const usersResponse = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!usersResponse.ok) throw new Error('Failed to fetch users');
      const usersData = await usersResponse.json();
      setUsers(usersData.users || []);

      // Fetch jobs
      const jobsResponse = await fetch('/api/jobs?status=all', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!jobsResponse.ok) throw new Error('Failed to fetch jobs');
      const jobsData = await jobsResponse.json();
      setJobs(jobsData.jobs || []);

      // Fetch permissions
      const permissionsResponse = await fetch('/api/admin/permissions', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!permissionsResponse.ok) throw new Error('Failed to fetch permissions');
      const permissionsData = await permissionsResponse.json();
      setPermissions(permissionsData.permissions || []);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({
      email: '',
      first_name: '',
      last_name: '',
      role: 'viewer',
      password: '',
    });
    setUserDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      password: '',
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editingUser 
        ? `/api/admin/users/${editingUser.id}` 
        : '/api/admin/users';
      const method = editingUser ? 'PUT' : 'POST';

      // Build request body, only including password if it's not empty
      const requestBody: any = {
        email: userForm.email,
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        role: userForm.role,
      };

      // Only include password if it's provided (not empty)
      if (userForm.password) {
        requestBody.password = userForm.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save user');
      }

      setSuccess(editingUser ? 'User updated successfully' : 'User created successfully');
      setUserDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleUserStatus = async (userId: string, isActive: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !isActive }),
      });

      if (!response.ok) throw new Error('Failed to update user status');
      
      setSuccess('User status updated');
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleManagePermissions = (user: User) => {
    setSelectedUser(user);
    // Get jobs this user already has permissions for
    const userPermissions = permissions.filter(p => p.user_id === user.id);
    setSelectedJobs(userPermissions.map(p => p.job_id));
    setPermissionDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/users/${selectedUser.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          job_ids: selectedJobs,
          permission_type: permissionType,
        }),
      });

      if (!response.ok) throw new Error('Failed to update permissions');
      
      setSuccess('Permissions updated successfully');
      setPermissionDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'hr': return 'warning';
      case 'manager': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Shield color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Admin Dashboard</Typography>
        </Box>
        <IconButton onClick={fetchData} title="Refresh">
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} data-testid="admin-tabs">
          <Tab icon={<Person />} label="Users" iconPosition="start" data-testid="admin-tab-users" />
          <Tab icon={<Security />} label="Permissions" iconPosition="start" data-testid="admin-tab-permissions" />
          <Tab icon={<Email />} label="Email Logs" iconPosition="start" data-testid="admin-tab-email-logs" />
          <Tab icon={<History />} label="Audit Logs" iconPosition="start" data-testid="admin-tab-audit-logs" />
        </Tabs>

        {/* Users Tab */}
        {tabValue === 0 && (
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">User Management</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddUser}
              >
                Add User
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((userData) => (
                    <TableRow key={userData.id}>
                      <TableCell>{userData.first_name} {userData.last_name}</TableCell>
                      <TableCell>{userData.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={userData.role}
                          size="small"
                          color={getRoleColor(userData.role) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={userData.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={userData.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {userData.last_login 
                          ? new Date(userData.last_login).toLocaleDateString()
                          : 'Never'
                        }
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(userData)}
                            title="Edit user"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleManagePermissions(userData)}
                            title="Manage permissions"
                          >
                            <Security fontSize="small" />
                          </IconButton>
                          <Button
                            size="small"
                            onClick={() => handleToggleUserStatus(userData.id, userData.is_active)}
                            disabled={userData.email === user?.email} // Don't let admin deactivate themselves
                          >
                            {userData.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Permissions Tab */}
        {tabValue === 1 && (
          <Box p={3}>
            <Typography variant="h6" mb={2}>Job Permissions Overview</Typography>
            
            {/* Permission System Explanation */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Automatic Access by Role:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • <strong>Admins & HR:</strong> Automatic full access to all jobs<br />
                • <strong>Managers:</strong> Automatic view access to all jobs (can be upgraded with explicit permissions)<br />
                • <strong>Viewers:</strong> Require specific job permissions to access anything
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Note:</strong> Only explicit permissions are shown in the table below. 
                Managers automatically have view access to all jobs even if not listed.
              </Typography>
            </Alert>

            {permissions.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Security sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No explicit permissions configured yet
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Explicit permissions allow you to grant higher access levels or give viewer-role users access to specific jobs.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click the <Security fontSize="small" sx={{ verticalAlign: 'middle' }} /> icon 
                  next to any user in the Users tab to assign job permissions.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Job</TableCell>
                      <TableCell>Permission Level</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {permissions.map((perm) => (
                      <TableRow key={perm.id}>
                        <TableCell>{perm.user_name}</TableCell>
                        <TableCell>{perm.job_title}</TableCell>
                        <TableCell>
                          <Chip
                            label={perm.permission_type}
                            size="small"
                            color={
                              perm.permission_type === 'manage' ? 'error' :
                              perm.permission_type === 'edit' ? 'warning' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Find the user and open permissions dialog
                              const user = users.find(u => u.id === perm.user_id);
                              if (user) handleManagePermissions(user);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Email Logs Tab */}
        {tabValue === 2 && (
          <Box p={3}>
            <EmailLogs refreshTrigger={Math.random()} />
          </Box>
        )}

        {/* Audit Logs Tab */}
        {tabValue === 3 && (
          <Box p={3} data-testid="admin-audit-logs-tab">
            <AuditLogs />
          </Box>
        )}
      </Paper>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="First Name"
              value={userForm.first_name}
              onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={userForm.last_name}
              onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value as User['role'] })}
                label="Role"
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            {!editingUser && (
              <TextField
                label="Password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                fullWidth
                required
                helperText="Required for new users"
              />
            )}
            {editingUser && (
              <TextField
                label="New Password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                fullWidth
                helperText="Leave blank to keep current password"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Permissions for {selectedUser?.first_name} {selectedUser?.last_name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select which jobs this user can access and their permission level.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Permission Level</InputLabel>
              <Select
                value={permissionType}
                onChange={(e) => setPermissionType(e.target.value as typeof permissionType)}
                label="Permission Level"
              >
                <MenuItem value="view">View Only</MenuItem>
                <MenuItem value="edit">View & Edit</MenuItem>
                <MenuItem value="manage">Full Manage</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              options={jobs}
              getOptionLabel={(option) => `${option.title} - ${option.department}`}
              value={jobs.filter(job => selectedJobs.includes(job.id))}
              onChange={(_, newValue) => {
                setSelectedJobs(newValue.map(job => job.id));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Jobs" placeholder="Choose jobs..." />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.title}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Permission Levels:</strong>
                <br />• <strong>View:</strong> Can view job and candidates
                <br />• <strong>Edit:</strong> Can edit job details and candidate information
                <br />• <strong>Manage:</strong> Can delete jobs and manage all aspects
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Role-based Access:</strong>
                <br />• <strong>Managers:</strong> Always have view access to all jobs (explicit permissions can upgrade this)
                <br />• <strong>Viewers:</strong> Need explicit permissions for each job they should access
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePermissions} variant="contained">
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;